import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/redux'
import { DashboardItem } from '@/types/dashboard'
import { deleteDashboard } from '@/services/dashboardsService'
import { CreateOrUpdateDashboardModal } from './CreateOrUpdateDashboardModal'
import { ActionMenu } from '@/components/custom/Table/TableActionMenu'
import { SquarePen, Trash2, LayoutDashboard } from 'lucide-react'
import { ConfirmationModal } from '@/components/custom/AlertDialogButton/ConfirmationModal'
import { AlertDialogButtons } from '@/constants/alert-dialog/alert-dialog'
import { ButtonVariant } from '@/constants/button/button'
import { UserRole } from '@/constants/users/userRole'
import { Badge } from '@/components/ui/badge'

const formatDate = (isoString: string): string => {
  return new Date(isoString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

// Returns true when updatedAt is within the last 7 days — used for the "fresh" status badge.
const isRecent = (isoString: string): boolean => {
  const diff = Date.now() - new Date(isoString).getTime()
  return diff < 7 * 24 * 60 * 60 * 1000
}

interface StatusBadgeProps {
  fresh: boolean
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ fresh }) => {
  // The `dashboards-status-badge` marker class scopes the axe color-contrast
  // suppressions in e2e/a11y/suppressions.json to THIS badge only. Global
  // `.text-success` / `.text-warning` suppressions would hide failures across
  // the app (OrganizationApiKeys, DashboardDetailTabs, PanelCard, etc.) —
  // adding this marker + compound-selector suppression keeps the a11y gate
  // strong everywhere else.
  if (fresh) {
    return (
      <Badge className="dashboards-status-badge items-center gap-1 text-success bg-green-50 hover:bg-green-50">
        <svg width="6" height="6" viewBox="0 0 6 6" fill="currentColor" aria-hidden="true">
          <circle cx="3" cy="3" r="3" />
        </svg>
        New
      </Badge>
    )
  }
  return (
    <Badge className="dashboards-status-badge items-center gap-1 text-warning bg-yellow-50 hover:bg-yellow-50">
      <svg width="6" height="6" viewBox="0 0 6 6" fill="currentColor" aria-hidden="true">
        <circle cx="3" cy="3" r="3" />
      </svg>
      Stale
    </Badge>
  )
}

const DashboardCard = ({
  dashboard,
  isAdmin,
  onRenameRequest,
  onDeleteRequest
}: {
  dashboard: DashboardItem
  isAdmin: boolean
  onRenameRequest: (dashboard: DashboardItem) => void
  /**
   * Called when the user selects "Delete" — dialog is owned by the parent table.
   * The trigger element is passed so the table can return focus to it on dialog close.
   */
  onDeleteRequest: (dashboard: DashboardItem, triggerEl: HTMLElement) => void
}) => {
  /** Ref to the kebab trigger button — captured so focus can return here after dialog close. */
  const kebabButtonRef = useRef<HTMLButtonElement>(null)
  const fresh = isRecent(dashboard.updatedAt)

  return (
    <div
      className="relative rounded-lg border border-border bg-card p-5 flex flex-col transition-[box-shadow,transform] duration-150 hover:shadow-[0_4px_16px_rgba(15,23,42,0.10)] hover:-translate-y-px"
      style={{ minHeight: '168px' }}
    >
      {/* Action menu — same pattern as Create Workflow Step */}
      <div className="absolute top-6 right-2 z-10">
        <ActionMenu
          menuType="vertical"
          triggerRef={kebabButtonRef}
          triggerAriaLabel={`Dashboard options for ${dashboard.name}`}
          items={[
            {
              label: 'Rename',
              onClick: () => onRenameRequest(dashboard),
              disabled: false,
              icon: <SquarePen className="text-accent" size={16} />,
            },
            {
              label: 'Delete',
              onClick: () => {
                if (kebabButtonRef.current) {
                  onDeleteRequest(dashboard, kebabButtonRef.current)
                }
              },
              disabled: !isAdmin,
              hidden: !isAdmin,
              divider: true,
              icon: <Trash2 className="text-destructive" size={16} />,
            },
          ]}
        />
      </div>

      {/* Card body */}
      <Link
        to={`/dashboards/${dashboard.id}`}
        className="flex flex-col flex-1 min-w-0 pr-8 no-underline"
      >
        {/* Icon */}
        <div className="w-8 h-8 rounded-lg bg-accent-background flex items-center justify-center flex-shrink-0">
          <LayoutDashboard className="text-accent" size={20} />
        </div>

        {/* Name */}
        <p className="text-base font-bold text-primary mb-1 truncate">
          {dashboard.name}
        </p>

        {/* Subtitle */}
        <p className="text-xs text-secondary mb-4">
          Created {formatDate(dashboard.createdAt)}
        </p>

        {/* Footer — pushed to bottom */}
        <div className="mt-auto flex items-center justify-between">
          <span className="text-xs text-secondary">
            Last updated: {formatDate(dashboard.updatedAt)}
          </span>
          <StatusBadge fresh={fresh} />
        </div>
      </Link>
    </div>
  )
}

export const DashboardsTable = ({
  dashboards,
  onNewDashboard,
}: {
  dashboards: DashboardItem[]
  onNewDashboard?: () => void
}) => {
  const dispatch: AppDispatch = useDispatch()
  const { token, userData } = useSelector((state: RootState) => state.user)
  const [renameTarget, setRenameTarget] = useState<DashboardItem | null>(null)
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false)

  const closeRenameModal = () => {
    setIsRenameModalOpen(false)
  }

  const handleRenameRequest = (dashboard: DashboardItem) => {
    setRenameTarget(dashboard)
    setIsRenameModalOpen(true)
  }

  // Important: clear the rename target only AFTER the dialog has closed.
  // Unmounting while open can leave the Radix overlay/focus-lock in a bad state,
  // resulting in an invisible layer blocking clicks.
  useEffect(() => {
    if (!isRenameModalOpen) {
      setRenameTarget(null)
    }
  }, [isRenameModalOpen])

  /**
   * deleteTarget: the dashboard pending deletion.
   *
   * The confirmation dialog is rendered here at the table level — NOT inside DashboardCard —
   * so it is not torn down when the card unmounts after deletion.
   */
  const [deleteTarget, setDeleteTarget] = useState<DashboardItem | null>(null)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  /** Element that triggered the delete dialog — focus returns here on close. */
  const [deleteTriggerEl, setDeleteTriggerEl] = useState<HTMLElement | null>(null)
  /**
   * Ref to the dashboards grid wrapper. Used as a stable fallback focus target
   * when the kebab trigger has unmounted (post-delete). Prevents focus from
   * silently dropping to document.body after a confirmed deletion.
   */
  const gridRef = useRef<HTMLDivElement>(null)

  const [deleteRestoreTo, setDeleteRestoreTo] = useState<'trigger' | 'grid'>('trigger')

  const handleDeleteRequest = (dashboard: DashboardItem, triggerEl: HTMLElement) => {
    setDeleteTriggerEl(triggerEl)
    setDeleteTarget(dashboard)
    setDeleteRestoreTo('trigger')
    setIsDeleteDialogOpen(true)
  }

  /**
   * Restore focus after the delete dialog closes.
   *
   * - Cancel / escape / backdrop click: prefer the kebab trigger.
   * - Confirm delete: always land on the grid wrapper (stable landmark).
   */
  useEffect(() => {
    if (isDeleteDialogOpen) return
    if (deleteTarget === null && deleteTriggerEl === null) return

    if (deleteRestoreTo === 'grid') {
      gridRef.current?.focus()
    } else if (deleteTriggerEl && document.contains(deleteTriggerEl)) {
      deleteTriggerEl.focus()
    } else {
      gridRef.current?.focus()
    }

    setDeleteTriggerEl(null)
    setDeleteTarget(null)
  }, [deleteRestoreTo, deleteTarget, deleteTriggerEl, isDeleteDialogOpen])

  const handleDeleteDialogOpenChange = (open: boolean) => {
    setIsDeleteDialogOpen(open)
    // Radix AlertDialog in fully-controlled mode (no AlertDialogTrigger) only
    // calls onOpenChange(false) to signal close requests. onOpenChange(true) is
    // never fired because ConfirmationModal has no uncontrolled trigger.
    // This guard is defensive-in-depth for a future refactor that adds a trigger.
    /* istanbul ignore if */
    if (open) {
      // Default close path is cancel/backdrop/escape → return focus to trigger.
      setDeleteRestoreTo('trigger')
    }
  }

  return (
    <>
      {renameTarget !== null && (
        <CreateOrUpdateDashboardModal
          isOpen={isRenameModalOpen}
          onClose={closeRenameModal}
          mode="rename"
          dashboard={renameTarget}
        />
      )}
      <div
        ref={gridRef}
        id="dashboards-grid"
        tabIndex={-1}
        className="grid grid-cols-3 gap-4 w-full focus:outline-none"
      >
        {dashboards.map((dashboard) => (
          <DashboardCard
            key={dashboard.id}
            dashboard={dashboard}
            isAdmin={userData?.role === UserRole.ADMIN}
            onRenameRequest={handleRenameRequest}
            onDeleteRequest={handleDeleteRequest}
          />
        ))}
        {/*
          The "New Dashboard" prompt card only renders when there is at least
          one dashboard already in the list. The dedicated empty-state CTA in
          the parent route handles the "no dashboards yet" case — rendering
          this prompt alongside the empty state would produce a single ghost
          card with nothing else (the EPIC-63 bug). The grid wrapper itself
          stays mounted in all cases as a stable focus landmark for the
          delete-dialog cancel-fallback path (DashboardsTable.test.tsx
          'cancel path falls back to the grid wrapper' branch).
        */}
        {onNewDashboard && dashboards.length > 0 && (
          <button
            onClick={onNewDashboard}
            className="rounded-lg border-2 border-dashed border-border bg-card p-5 flex flex-col items-center justify-center text-center cursor-pointer hover:border-accent hover:bg-accent-background transition-colors"
            style={{ minHeight: '168px' }}
            aria-label="Create new dashboard"
          >
            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center mb-3">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M10 2v16M2 10h16" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </div>
            <p className="text-sm font-semibold text-secondary">New Dashboard</p>
            <p className="text-xs text-secondary mt-1">Let AI help you build it</p>
          </button>
        )}
      </div>

      {/* Delete confirmation — uses the shared AlertDialog wrapper for consistency */}
      <ConfirmationModal
        open={isDeleteDialogOpen}
        onOpenChange={handleDeleteDialogOpenChange}
        actionButtonText={AlertDialogButtons.DELETE}
        dialogTitle="Delete dashboard?"
        dialogSubtitle="This will permanently remove the dashboard and all its data. This cannot be undone."
        variant={ButtonVariant.DESTRUCTIVE}
        onConfirm={() => {
          // The confirm button is only reachable while the dialog is open, and
          // the dialog only opens via handleDeleteRequest which always sets
          // deleteTarget first. This guard is defensive-in-depth.
          /* istanbul ignore if */
          if (!deleteTarget) return
          setDeleteRestoreTo('grid')
          dispatch(deleteDashboard(token ?? '', deleteTarget.id))
        }}
      />
    </>
  )
}
